<template>
  <div>
    <div class="row page-title-header">
      <div class="col-12">
        <BreadCrumb 
          parentRouteName="UploadFileList" 
          :extra-items="isReadOnly ? [
            {
              text: uploadFile?.name ?? '',
            }
          ] : isEditing ? [
            {
              text: uploadFile?.name ?? '',
              to: 'UploadFileView',
              params: $route.params.upload_file_id
            },
            displayText('upload_file.edit_file_title', '編輯檔案')
          ] : [
            displayText('upload_file.create_file_title', '新增檔案')
          ]" 
        />
      </div>
    </div>
    <div class="row">
      <div class="col-12">
        <b-overlay :show="showLoading">
          <b-card>
            <div class="row d-flex mb-4 mb-xl-2 justify-content-between">
              <h4 class="col-12 col-xl-6 mb-2 mb-xl-0 font-weight-bold">
                {{
                  isReadOnly ?
                    displayText('upload_file.view_file_title', "查看檔案") :
                    isEditing ?
                      displayText('upload_file.edit_file_title', "編輯檔案") :
                      displayText('upload_file.create_file_title', "新增檔案")
                }}
              </h4>
              <div class="col-12 col-xl-8 mt-3">
                <validation-error-alert
                  v-if="validationErrors"
                  :errors="validationErrors"
                ></validation-error-alert>

                <b-form-group
                  label-cols="12"
                  label-cols-lg="2"
                  label-size="sm"
                  :label=" '*' + displayText('upload_file.field_name', '檔案名稱')"
                >
                  <b-form-input
                    class="mb-2 mr-sm-2 mb-sm-0"
                    type="text"
                    :state="v$.uploadFile.name.$error ? false : null"
                    v-model="uploadFile.name"
                    :readonly="isReadOnly || isEditing"
                  ></b-form-input>
                  <b-form-invalid-feedback :state="!v$.uploadFile.name.$error">
                    {{ displayText('upload_file.validation_required', '此欄位為必填') }}
                  </b-form-invalid-feedback>
                </b-form-group>
                <b-form-group
                  label-cols="12"
                  label-cols-lg="2"
                  label-size="sm"
                  :label="'*' + displayText('upload_file.field_url', 'URL')"
                >
                  <button
                    class="
                      btn btn-link
                      px-2
                      text-dark
                      align-self-start
                      upload-button
                    "
                  >
                    <b-form-file @change="upload($event)" :disabled="isReadOnly" placeholder="尚未選擇檔案" browse-text="瀏覽"/>
                    <a
                      v-if="!!uploadFile.file_url"
                      @click="handleDelete"
                      class="btn-sm "
                      :class="isReadOnly ? 'btn-outline-secondary' : 'btn-outline-danger'"
                    >
                      <i class="fa fa-trash-o"></i>
                    </a>
                    <a :href="uploadFile.file_url" target="_blank" style="word-break: break-all;"
                      ><small>
                        {{ displayText('upload_file.field_upload_file_current_path', '目前路徑') }}：{{ uploadFile.file_url || displayText('upload_file.field_upload_file_not_uploaded', '未上傳') }}
                      </small></a
                    >
                  </button>
                  <b-form-invalid-feedback
                    :state="!v$.uploadFile.file_url.$error"
                  >
                    {{ displayText('upload_file.validation_required', '此欄位為必填') }}
                  </b-form-invalid-feedback>
                </b-form-group>

                <b-form-group
                  label-cols="12"
                  label-cols-lg="2"
                  label-size="sm"
                  :label="displayText('upload_file.field_description', '檔案描述')"
                >
                  <b-textarea
                    class="mb-2 mr-sm-2 mb-sm-0"
                    type="text"
                    :state="v$.uploadFile.description.$error ? false : null"
                    v-model="uploadFile.description"
                    :readonly="isReadOnly"
                  ></b-textarea>
                  <b-form-invalid-feedback
                    :state="!v$.uploadFile.description.$error"
                  >
                    {{ displayText('upload_file.validation_required', '此欄位為必填') }}
                  </b-form-invalid-feedback>
                </b-form-group>
                <b-form-group
                  label-cols="12"
                  label-cols-lg="2"
                  label-size="sm"
                  :label="displayText('upload_file.field_category_type', '檔案分類')"
                  v-if="categoryOptions.length > 0"
                >
                  <b-form-select
                    class="mb-2 mr-sm-2 mb-sm-0"
                    v-model="uploadFile.category"
                    :options="categoryOptions"
                    :disabled="isReadOnly"
                  ></b-form-select>
                </b-form-group>

                <b-form-group
                  label-cols="12"
                  label-cols-lg="2"
                  label-size="sm"
                  :label="displayText('upload_file.field_is_enabled', '是否啟用')"
                >
                  <b-form-checkbox
                    class="mb-2 mr-sm-2 mb-sm-0"
                    v-model="uploadFile.is_enabled"
                    :disabled="isReadOnly"
                  ></b-form-checkbox>
                </b-form-group>
                <b-form-group
                  label-cols="12"
                  label-cols-lg="2"
                  label-size="sm"
                  :label="displayText('upload_file.field_start_at', '開始時間')"
                >
                  <div class="d-flex">
                    <div class="w-50 mr-2">
                      <datepicker
                        v-model="uploadFile.start_at"
                        bootstrap-styling
                        format="yyyy-MM-dd"
                        :language="zh"
                        :disabled="isReadOnly"
                        clear-button
                      ></datepicker>
                    </div>
                    <div class="w-50">
                      <vue-timepicker
                        format="HH:mm"
                        v-model="startTime"
                        :input-class="['form-control']"
                        :disabled="isReadOnly"
                      ></vue-timepicker>
                    </div>
                  </div>
                  <b-form-invalid-feedback
                    :state="!v$.uploadFile.start_at.$error"
                  >
                    {{ displayText('upload_file.validation_required', '此欄位為必填') }}
                  </b-form-invalid-feedback>
                </b-form-group>
                <b-form-group
                  label-cols="12"
                  label-cols-lg="2"
                  label-size="sm"
                  :label="displayText('upload_file.field_end_at', '結束時間')"
                >
                  <div class="d-flex">
                    <div class="w-50 mr-2">
                      <datepicker
                        v-model="uploadFile.end_at"
                        bootstrap-styling
                        format="yyyy-MM-dd"
                        :language="zh"
                        :disabled="isReadOnly"
                        clear-button
                      ></datepicker>
                    </div>
                    <div class="w-50">
                      <vue-timepicker
                        format="HH:mm"
                        v-model="endTime"
                        :input-class="['form-control']"
                        :disabled="isReadOnly"
                      ></vue-timepicker>
                    </div>
                  </div>
                  <b-form-invalid-feedback
                    :state="!v$.uploadFile.end_at.$error"
                  >
                    {{ displayText('upload_file.validation_required', '此欄位為必填') }}
                  </b-form-invalid-feedback>
                </b-form-group>
              </div>
            </div>
            <div class="d-flex justify-content-center">
              <b-button
                class="mr-3"
                @click="handleCancel"
                variant="outline-danger"
                >{{ displayText('upload_file.go_back_button_text', '返回') }}
              </b-button>
              <b-button
                v-if="!isReadOnly"
                @click="handleSubmit"
                variant="success"
                >{{ displayText('upload_file.submit_button_text', '儲存') }}</b-button
              >
            </div>
          </b-card>
        </b-overlay>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState, mapGetters } from "vuex";
import useVuelidate from "@vuelidate/core";
import { required } from "@vuelidate/validators";
import uploadFileApi from "../../../apis/upload-file";
import Datepicker from "vuejs-datepicker";
import { zh } from "vuejs-datepicker/dist/locale";
import VueTimepicker from "vue2-timepicker";
import moment from "moment";
import fileApi from "@/apis/file";
import { displayText } from '@/utils/dashboardUtils'
import BreadCrumb from "@/components/Dashboard/BreadCrumb";

export default {
  setup: () => ({ v$: useVuelidate() }),
  components: { Datepicker, VueTimepicker, BreadCrumb },
  validations() {
    const nameRegex = (value) => {
      const illegalChars = /[\\/:*?"<>|]/;
      return !illegalChars.test(value)
    };
    return {
      uploadFile: {
        org_id: {},
        name: { required, nameRegex },
        description: {},
        is_enabled: {},
        file_url: { required },
        file_id: { required },
        start_at: {},
        end_at: {},
        category:{}
      },
    };
  },

  data() {
    return {
      zh,
      showLoading: false,
      isEditing: true,
      validationErrors: null,
      categoryOptions: [],
      uploadFile: {
        org_id: null,
        name: null,
        description: null,
        is_enabled: true,
        file_id: null,
        file_url: null,
        start_at: null,
        end_at: null,
      },
      file: null,
      showLoadingUpload: false,
      maxUploadSize:10
    };
  },

  computed: {
    ...mapState("general", {
      organization: (state) => state.organization,
    }),
    ...mapGetters({
      getModuleConfig: "dashboardModule/getConfig",
    }),
    ...mapGetters("route", ["routeQuery"]),

    startTime: {
      get() {
        return moment(this.uploadFile.start_at).format("HH:mm");
      },
      set(value) {
        if (!value) return
        let [hour, minute] = value.split(":").map(Number);
        this.uploadFile.start_at = moment(this.uploadFile.start_at).set({ hour, minute }).format("YYYY-MM-DD HH:mm");
      },
    },
    endTime: {
      get() {
        return moment(this.uploadFile.end_at).format("HH:mm");
      },
      set(value) {
        if (!value) return
        let [hour, minute] = value.split(":").map(Number);
        this.uploadFile.end_at = moment(this.uploadFile.end_at).set({ hour, minute }).format("YYYY-MM-DD HH:mm");
      },
    },

    isReadOnly() {
      return this.$route.name === "UploadFileView";
    },
  },
  mounted() {
    this.fetchCategoryOptions();
    if (!this.$route.params.upload_file_id) {
      this.isEditing = false;
    } else {
      this.fetchUploadFile();
      this.isEditing = true;
    }
    this.maxUploadSize = this.getModuleConfig("upload_files", "max_upload_size") ?? 10
  },
  methods: {
    displayText,
    fetchCategoryOptions() {
      const data = this.getModuleConfig("upload_files", "upload_files_category") ?? false
      if (!data) return;
      this.categoryOptions = [
        ...data.sort((a, b) => a.order - b.order).map((source) => {
          return {
            text: source.text,
            value: source.key,
          };
        }),
      ];
    },
    async upload(e) {
      try {
        if (e.target.files[0].size > this.maxUploadSize * 1024 * 1024) {
          this.$swal.fire({
            title: displayText('upload_file.api_upload_file_failed_response_text', '上傳檔案失敗'),
            type: "error",
            text: `檔案大小不可超過${this.maxUploadSize}MB`,
          });
          return;
        }
        this.showLoading = true;
        let { data } = await fileApi.upload(e.target.files[0], null, true);
        this.uploadFile.file_url = data.data.file.url;
        this.uploadFile.file_id = data.data.file.id;
      } catch (error) {
        this.$swal.fire({
          title: displayText('upload_file.api_upload_file_failed_response_text', '上傳檔案失敗'),
          type: "error",
        });
      } finally {
        this.showLoading = false;
      }
    },
    async fetchUploadFile() {
      this.showLoading = true;
      const response = await uploadFileApi.getUploadFile(
        this.organization,
        this.$route.params.upload_file_id
      );
      this.uploadFile = response.data.data;
      this.showLoading = false;
    },
    handleDelete() {
      if (this.isReadOnly) return;
      this.uploadFile.file_url = null
    },
    async handleSubmit() {
      if (!this.uploadFile.start_at) this.uploadFile.start_at = null
      if (!this.uploadFile.end_at) this.uploadFile.end_at = null
      const result = await this.v$.$validate();
      if (!result) return;

      try {
        if (this.isEditing) {
          let response = await uploadFileApi.updateUploadFile(
            this.organization,
            this.uploadFile
          );
          if (response.status && response.status === 200) {
            this.$swal.fire({
              title: displayText('upload_file.api_update_success_response_text', '更新成功'),
              type: "success",
            });

            this.$router.push({
              name: "UploadFileList",
              query: this.routeQuery,
            });
          }
        } else {
          let response = await uploadFileApi.storeUploadFile(this.uploadFile);

          if (response.status && response.status === 200) {
            this.$swal.fire({
              title: displayText('upload_file.api_create_success_response_text', '新增成功'),
              type: "success",
            });

            this.$router.push({
              name: "UploadFileList",
              query: this.routeQuery,
            });
          }
        }
      } catch (error) {
        if (error.response.status === 422 && error.response.data.message) {
          this.validationErrors = error.response.data.message;
        } else {
          this.$swal.fire({
            title: this.isEditing ? displayText('upload_file.api_update_failed_response_text', '更新失敗') : displayText('upload_file.api_create_failed_response_text', '新增失敗'),
            type: "error",
          });
        }
      }
    },
    handleCancel() {
      this.$router.push({ name: "UploadFileList", query: this.routeQuery });
    },
  },
};
</script>
